import { useEffect, useRef, useState } from 'react';
import { format, isPast } from 'date-fns';
import { toast } from '@/components/ui/use-toast';
import { ExportTypeEnum, MarketStatus } from '@sportspredict/shared';
import { INITIAL_NEW_MARKET } from '@/constants';
import { useExportFileMutation } from '@/store/api';
import {
	useBulkCreateMarketsMutation,
	useSettleMarketMutation,
} from '@/store/api/market-manage.api';
import {
	formatImportDate,
	parseCSVLine,
	validateMarketDates,
	chunkArray,
	getEventOpeningTime,
	asApiError,
} from '@/utils';
import { Market } from '../../api';

export function useEventMarketCrud({
	eventId,
	event,
	currentCrowdLobby,
	markets,
	setMarkets,
	loadMarketData,
	fetchAllFilteredMarkets,
	tradeFilterLobbyId,
	setExportTradesJobId,
	setExportedTradesFile,
	bulkMarkets,
	setBulkMarkets,
	marketCategories = [],
}: {
	eventId: any;
	event: any;
	currentCrowdLobby: any;
	markets: any[];
	setMarkets: (updater: any) => void;
	loadMarketData: () => Promise<void>;
	fetchAllFilteredMarkets?: () => Promise<any[]>;
	tradeFilterLobbyId: any;
	setExportTradesJobId: (value: any) => void;
	setExportedTradesFile: (value: any) => void;
	bulkMarkets: any[];
	setBulkMarkets: (updater: any) => void;
	marketCategories?: any[];
}) {
	const [bulkCreateMarkets] = useBulkCreateMarketsMutation();
	const [settleMarket] = useSettleMarketMutation();

	const [isAddingMarket, setIsAddingMarket] = useState(false);
	const [newMarket, setNewMarket] = useState(INITIAL_NEW_MARKET);

	const [isBulkCreatingMarkets, setIsBulkCreatingMarkets] = useState(false);

	const [isImportingMarkets, setIsImportingMarkets] = useState(false);
	const [isUploadingFile, setIsUploadingFile] = useState(false);
	const [importedMarkets, setImportedMarkets] = useState<any[]>([]);
	const [fileUploaded, setFileUploaded] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [settlingMarketId, setSettlingMarketId] = useState<any>(null);
	const [settlementProgress, setSettlementProgress] = useState(0);

	const [bulkSlateId, setBulkSlateId] = useState('');
	const [bulkMatchId, setBulkMatchId] = useState<any>(null);
	const [bulkLobbyMode, setBulkLobbyMode] = useState('all');
	const [bulkSelectedLobbyIds, setBulkSelectedLobbyIds] = useState<any[]>([]);

	const [globalClosingDate, setGlobalClosingDate] = useState<any>(null);
	const [globalOpeningDate, setGlobalOpeningDate] = useState<any>(null);

	const getMarketStatus = (openingTime: any) => {
		let marketStatus = MarketStatus.Open;
		if ((getEventOpeningTime(event) ?? new Date()) > new Date()) {
			marketStatus = MarketStatus.Queue;
		} else {
			if (openingTime && !isPast(openingTime)) {
				marketStatus = MarketStatus.Queue;
			}
		}
		return marketStatus;
	};

	const handleCreateMarket = async (marketPayload: any) => {
		try {
			const isMarketDatesValid = validateMarketDates(event, marketPayload);

			if (!isMarketDatesValid) {
				toast({
					variant: 'destructive',
					title: 'Validation Error',
					description: 'Market dates must be within event date range',
				});
				return;
			}

			const status = marketPayload?.status || getMarketStatus(marketPayload?.opening_time);
			const market = await Market.create({
				...marketPayload,
				status,
				opening_time:
					marketPayload?.opening_time || (getEventOpeningTime(event) ?? new Date()).toISOString(),
				market_open_delay: currentCrowdLobby?.market_open_delay,
				game_start_date: currentCrowdLobby?.game_start_date,
			});

			toast({ title: 'Success', description: 'Market created successfully' });
			setNewMarket(INITIAL_NEW_MARKET);
			setIsAddingMarket(false);

			setMarkets((prev: any[]) =>
				prev.some((m) => m.id === market.id) ? prev : [market, ...prev]
			);
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: asApiError(error).message?.includes('429')
					? 'Too many requests. Please wait a moment.'
					: 'Failed to create market. Please try again.',
			});
			console.error(error);
			throw new Error('Error creating market');
		}
	};

	useEffect(() => {
		if (!isAddingMarket) {
			setNewMarket(INITIAL_NEW_MARKET);
		}
	}, [isAddingMarket]);

	// Add function to handle CSV file upload and parsing
	const handleFileUpload = async (e: any) => {
		const file = e.target.files[0];
		if (!file) return;

		try {
			setIsUploadingFile(true);

			const text = await file.text();
			const lines = text.trim().split('\n');

			const headers = lines[0].split(',').map((h: string) => h.trim());

			const parsedMarkets = lines.slice(1).map((line: string) => {
				const values = parseCSVLine(line);
				return headers.reduce((acc: any, key: string, idx: number) => {
					acc[key] = values[idx] || '';
					return acc;
				}, {});
			});

			const validMarkets = parsedMarkets
				.filter(
					(item: any) =>
						item.question &&
						!isNaN(parseFloat(item.initial_value)) &&
						parseFloat(item.initial_value) >= 1 &&
						parseFloat(item.initial_value) <= 99
				)
				.map((item: any) => ({
					question: item.question.trim(),
					initialValue: parseFloat(item.initial_value) || 50,
					closingDate: formatImportDate(item.closing_date),
					openingTime: formatImportDate(item.opening_time),
				}));

			if (validMarkets.length === 0) {
				throw new Error('No valid market data found in file');
			}

			setImportedMarkets(validMarkets);
			setFileUploaded(true);

			toast({
				title: 'File Processed',
				description: `Successfully extracted ${validMarkets.length} markets`,
			});
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: asApiError(error).message || 'Failed to process file',
			});
			setFileUploaded(false);
			if (fileInputRef.current) fileInputRef.current.value = '';
			console.error(error);
			throw new Error('Error processing CSV');
		} finally {
			setIsUploadingFile(false);
		}
	};

	// Add function to create markets from imported data
	const handleImportMarkets = async () => {
		if (importedMarkets.length === 0) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'No valid markets to import',
			});
			return;
		}

		try {
			const now = new Date();

			// Process in batches to avoid rate limiting
			let createdCount = 0;
			for (const market of importedMarkets) {
				// Determine market status based on opening time
				const opening_time = market.openingTime
					? new Date(market.openingTime) > now
						? new Date(market.openingTime).toISOString()
						: (getEventOpeningTime(event) ?? new Date()).toISOString()
					: (getEventOpeningTime(event) ?? new Date()).toISOString();

				const closing_date = market?.closingDate
					? new Date(market.closingDate).toISOString()
					: null;

				const isMarketDatesValid = validateMarketDates(event, { closing_date, opening_time });

				if (!isMarketDatesValid) {
					toast({
						variant: 'destructive',
						title: 'Validation Error',
						description: `Market ${market.question} dates must be within event date range`,
					});
					return;
				}

				const marketData = {
					event_id: eventId,
					question: market.question,
					initial_value: market.initialValue,
					current_value: market.initialValue,
					status: getMarketStatus(opening_time),
					start_date: now.toISOString(),
					closing_date,
					opening_time,
				};

				await Market.create(marketData);

				createdCount++;

				// Add delay between requests to avoid rate limiting
				if (importedMarkets.length > 5) {
					await new Promise((resolve) => setTimeout(resolve, 200));
				}
			}

			toast({
				title: 'Success',
				description: `Created ${createdCount} markets from import`,
			});

			// Reset states and close dialog
			setImportedMarkets([]);
			setFileUploaded(false);
			setIsImportingMarkets(false);

			// Refresh markets list
			await loadMarketData();
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: asApiError(error).message || 'Failed to import markets',
			});
			console.error(error);
			throw new Error('Error importing markets');
		}
	};

	// Function to add new market row
	const addMarketRow = () => {
		setBulkMarkets([
			...bulkMarkets,
			{ question: '', initial_value: 50, category_id: '', isSelectedToCreate: true },
		]);
	};

	const removeMarketRow = (index: number) => {
		setBulkMarkets(bulkMarkets.filter((_, i) => i !== index));
	};

	// Function to update market row
	const updateMarketRow = (index: number, field: string, value: any) => {
		const updatedMarkets = [...bulkMarkets];
		updatedMarkets[index] = {
			...updatedMarkets[index],
			[field]: value,
		};
		setBulkMarkets(updatedMarkets);
	};

	// Modify handleBulkCreateMarkets to use retry logic
	const handleBulkCreateMarkets = async () => {
		try {
			const marketsToCreate = bulkMarkets.filter((market) => market.isSelectedToCreate);

			if (!marketsToCreate.length) {
				return;
			}

			// Validate inputs
			const showMarketGroups = marketCategories.length > 0;
			const invalidMarkets = marketsToCreate.filter(
				(market: any) =>
					!market.question ||
					market.initial_value < 1 ||
					market.initial_value > 99 ||
					(!market.ext_api_market_id && showMarketGroups && !market.category_id)
			);

			if (invalidMarkets.length > 0) {
				toast({
					variant: 'destructive',
					title: 'Validation Error',
					description:
						'Please fill in all required fields, ensure initial values are between 1 and 99, and select a market group',
				});
				return;
			}

			const payload = marketsToCreate.map((market: any) => {
				const opening_time =
					market?.opening_time ||
					(globalOpeningDate ? new Date(globalOpeningDate).toISOString() : null) ||
					(getEventOpeningTime(event) ?? new Date()).toISOString();

				return {
					question: market.question.trim(),
					initial_value: Number(market.initial_value),
					current_value: Number(market.initial_value),
					opening_time,
					start_date: market.start_date || new Date().toISOString(),
					closing_date:
						market.closing_date ||
						(globalClosingDate ? new Date(globalClosingDate).toISOString() : null),
					status: market?.status || getMarketStatus(opening_time),
					match_id: market?.match_id || bulkMatchId || null,
					category_id: market.category_id || null,
					ext_api_market_id: market.ext_api_market_id || null,
					ext_api_market: market?.ext_api_market || null,
					ext_api_name: market?.ext_api_name || null,
					stat_threshold: market?.stat_threshold || null,
					market_open_delay: currentCrowdLobby?.market_open_delay,
					game_start_date: currentCrowdLobby?.game_start_date,
					lobby_ids: bulkLobbyMode === 'all' ? [] : bulkSelectedLobbyIds,
				};
			});

			const chunks = chunkArray(payload, 50);

			let totalCreated = 0;
			let totalFailed = 0;

			for (const chunk of chunks) {
				try {
					await bulkCreateMarkets({
						event_id: eventId,
						data: chunk,
					}).unwrap();
					totalCreated += chunk.length;
				} catch (e) {
					totalFailed += chunk.length;
					console.error('Chunk failed:', e);
				}
			}

			setIsBulkCreatingMarkets(false);
			setBulkMarkets([
				{ question: '', initial_value: 50, category_id: '', isSelectedToCreate: true },
			]);
			setGlobalClosingDate(null);
			setGlobalOpeningDate(null);
			setBulkLobbyMode('all');
			setBulkSelectedLobbyIds([]);

			if (totalFailed === 0) {
				toast({
					title: 'Success',
					description: `${totalCreated} markets created successfully`,
				});
			} else {
				toast({
					variant: 'destructive',
					title: 'Partial Success',
					description: `Created: ${totalCreated}, Failed: ${totalFailed}`,
				});
			}
		} catch (error) {
			console.error(error);
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to create markets',
			});
			throw new Error('Error bulk creating markets');
		}
	};

	// Add a function to export markets to CSV
	const exportMarketsToCSV = async () => {
		try {
			const fullMarkets = (await fetchAllFilteredMarkets?.()) ?? markets;

			// Format the markets data
			const csvData = fullMarkets.map((market) => ({
				market_id: market.id,
				question: market.question,
				context: market?.context ?? '',
				initial_value: market.initial_value,
				current_value: market.current_value,
				status: market.status,
				opening_time: market?.opening_time ? new Date(market.opening_time).toLocaleString() : '',
				closing_date: market?.closing_date ? new Date(market.closing_date).toLocaleString() : '',
			}));

			// Create CSV string
			let csvContent =
				'market_id,question,context,initial_value,current_value,status,opening_time,closing_date\n';

			csvData.forEach((item) => {
				// Escape quotes and special characters in question
				const escapedQuestion = item.question.replace(/"/g, '""');

				// Build CSV row
				csvContent +=
					`"${item.market_id}",` +
					`"${escapedQuestion}",` +
					`${item.context},` +
					`${item.initial_value},` +
					`${item.current_value},` +
					`"${item.status}",` +
					`"${item.opening_time}",` +
					`"${item.closing_date}"\n`;
			});

			// Create a blob and download
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');

			// Create filename with event title and date
			const eventTitle = event.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'event';
			const date = format(new Date(), 'yyyy-MM-dd');
			const filename = `${eventTitle}_markets_${date}.csv`;

			link.href = url;
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			toast({
				title: 'Success',
				description: `Exported ${fullMarkets.length} markets to CSV`,
			});
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to export markets',
			});
			console.error(error);
			throw new Error('Error exporting markets');
		}
	};

	const [exportFile] = useExportFileMutation();

	const exportTradesToCSV = async () => {
		try {
			const { id } = await (exportFile as any)({
				exportType: ExportTypeEnum.TRADES,
				eventId,
				lobbyId: tradeFilterLobbyId,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			}).unwrap();

			setExportTradesJobId(id);

			toast({
				title: 'Pending',
				description: `File exporting`,
			});
		} catch (error) {
			setExportTradesJobId(null);
			setExportedTradesFile(null);

			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to export trades',
			});
			console.error(error);
			throw new Error('Error exporting trades');
		}
	};

	const handleSettleMarket = async (market: any, settledValue: any) => {
		setSettlingMarketId(market.id);
		setSettlementProgress(0);

		try {
			await settleMarket({ marketId: market.id, settledValue }).unwrap();
			setSettlementProgress(100);
		} catch (err: unknown) {
			toast({
				variant: 'destructive',
				title: 'Settlement Error',
				description: asApiError(err).message || 'Failed to settle market. Please try again.',
			});
			console.error(err);
			throw new Error('Error settling market');
		} finally {
			setSettlingMarketId(null);
			setSettlementProgress(0);
		}
	};

	return {
		isAddingMarket,
		setIsAddingMarket,
		newMarket,
		setNewMarket,
		isBulkCreatingMarkets,
		setIsBulkCreatingMarkets,
		isImportingMarkets,
		setIsImportingMarkets,
		isUploadingFile,
		importedMarkets,
		setImportedMarkets,
		fileUploaded,
		setFileUploaded,
		fileInputRef,
		settlingMarketId,
		settlementProgress,
		bulkSlateId,
		setBulkSlateId,
		bulkMatchId,
		setBulkMatchId,
		bulkLobbyMode,
		setBulkLobbyMode,
		bulkSelectedLobbyIds,
		setBulkSelectedLobbyIds,
		globalClosingDate,
		setGlobalClosingDate,
		globalOpeningDate,
		setGlobalOpeningDate,
		handleCreateMarket,
		handleFileUpload,
		handleImportMarkets,
		addMarketRow,
		removeMarketRow,
		updateMarketRow,
		handleBulkCreateMarkets,
		exportMarketsToCSV,
		exportTradesToCSV,
		handleSettleMarket,
	};
}
